import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Calendar, AlertCircle, CheckCircle, XCircle, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import apiAuth from "@/api/axios";
import { API } from "@/App";
import { useAuth } from "@/hooks/AuthContext";

export const NOTIFICATION_TYPES = {
  LEAVE_REQUEST: "leave_request",
  SYSTEM_ALERT: "system_alert",
  ANNOUNCEMENT: "announcement",
  TIMESHEET_APPROVAL: "MONTHLY_TIMESHEET_APPROVAL",
  GENERIC: "generic",
};

// Notification model
export class Notification {
  constructor(data) {
    this.id = data.id || data._id;
    this.type = data.type;
    this.title = data.title;
    this.message = data.message;
    this.timestamp = data.timestamp;
    this.read = data.read || false;
    this.isViewed = data.isViewed || false;
    this.metadata = data.metadata || {};
    this.link = data.link;
  }

  static createFromApiData(apiData, type) {
    switch (type) {
      case NOTIFICATION_TYPES.LEAVE_REQUEST:
        return new Notification({
          id: apiData._id,
          type,
          title: "Leave Request",
          message: `${apiData.user_name} applied for ${apiData.leave_type || apiData.type} leave`,
          timestamp: apiData.reason || "",
          metadata: {
            fromDate: apiData.start_date,
            toDate: apiData.end_date,
            leaveType: apiData.leave_type || apiData.type,
            leaveBalance: apiData.leave_balance,
            userName: apiData.user_name,
            userId: apiData.user_id,
            status: apiData.status,
          },
          read: false,
          isViewed: true, // Auto-view leave requests if fetched here? Or false. Let's use false.
        });

      case NOTIFICATION_TYPES.TIMESHEET_APPROVAL:
        return new Notification({
          id: apiData._id,
          type,
          title: apiData.title || "Timesheet Approved",
          message: apiData.message,
          timestamp: new Date(apiData.createdAt).toLocaleString(),
          read: apiData.isRead || false,
          metadata: apiData.metadata || {},
        });

      default:
        return new Notification({
          id: apiData._id,
          type: type || NOTIFICATION_TYPES.GENERIC,
          title: apiData.title || "Notification",
          message: apiData.message,
          timestamp: new Date(apiData.createdAt).toLocaleString(),
          read: apiData.isRead || false,
          isViewed: apiData.isViewed || false,
          metadata: apiData.metadata || {},
        });
    }
  }

  getIcon() {
    switch (this.type) {
      case NOTIFICATION_TYPES.LEAVE_REQUEST:
        return <Calendar size={16} />;
      case NOTIFICATION_TYPES.SYSTEM_ALERT:
        return <AlertCircle size={16} />;
      case NOTIFICATION_TYPES.TIMESHEET_APPROVAL:
        return <CheckCircle size={16} className="text-green-500" />;
      default:
        return <Bell size={16} />;
    }
  }
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [isLoading, setIsLoading] = useState(true);

  const fetchNotifications = async (pageArg) => {
    if (!user?._id && !user?.id) return;

    const pageToFetch = typeof pageArg === 'number' ? pageArg : pagination.page;

    try {
      setIsLoading(true);

      let leaveNotifications = [];
      if (user?.role === "ADMIN" || user?.role === "PM") {
        const leaveRes = await apiAuth.get(`${API}/calendar/leave-requests/pending`);
        leaveNotifications = (leaveRes.data.requests || []).map((req) =>
          Notification.createFromApiData(req, NOTIFICATION_TYPES.LEAVE_REQUEST)
        );
      }

      const notifRes = await apiAuth.get(`${API}/notification`, { 
        params: { 
          page: pageToFetch, 
          limit: 20,
          includeViewed: true 
        } 
      });

      
      const genericNotifications = (notifRes.data.notifications || notifRes.data || []).map((n) =>
        Notification.createFromApiData(n, n.type || NOTIFICATION_TYPES.GENERIC)
      );
      
      if (notifRes.data.pagination) {
        setPagination(notifRes.data.pagination);
      }

      const allNotifications = [...leaveNotifications, ...genericNotifications].map(
        (n, idx) => ({ ...n, uniqueKey: `${n.id}-${idx}` })
      );

      setNotifications(allNotifications);
      setUnreadCount(allNotifications.filter((n) => !n.read).length);
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setIsLoading(false);
    }
  };
  
  const markAllAsRead = async () => {
    try {
      await apiAuth.patch(`${API}/notification/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  
    const interval = setInterval(fetchNotifications, 15000);
    return () => clearInterval(interval);
  }, [user]);
  

  const handleLeaveAction = async (id, action) => {
    try {
      await apiAuth.post(`${API}/calendar/leave-requests/${id}/decision`, {
        decision: action === "approved" ? "APPROVED" : "REJECTED",
        approver_id: user?.id,
        approval_justification:
          action === "approved" ? "Approved by manager" : "Declined by manager",
      });

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? {
                ...n,
                read: true,
                metadata: { ...n.metadata, status: action === "approved" ? "APPROVED" : "REJECTED" },
              }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error(`Failed to ${action} leave request`, err);
    }
  };

  const handleMarkAsRead = async (id, event) => {
    if (event) {
      event.stopPropagation();
    }
    
    try {
      await apiAuth.post(`${API}/notification/viewed`, { ids: [id] });
      
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id
            ? { ...n, read: true, isViewed: true }
            : n
        )
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (err) {
      console.error("Failed to mark notification as read", err);
    }
  };

  const renderNotificationContent = (notification) => {
    const handleClick = () => {
      if (notification.type === NOTIFICATION_TYPES.TIMESHEET_APPROVAL && notification.metadata.userId) {
        navigate(`/admin/hr/${notification.metadata.userId}`);
      }
    };

    if (notification.type === NOTIFICATION_TYPES.LEAVE_REQUEST) {
      return (
        <div className="flex justify-between w-full gap-4 p-4 border-b">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{notification.title}</span>
              {!notification.read && <span className="h-2 w-2 rounded-full bg-primary"></span>}
            </div>
            <p className="text-muted-foreground">{notification.message}</p>
            <p className="text-xs text-muted-foreground mt-1">
              <b>{notification.metadata.fromDate}</b> → <b>{notification.metadata.toDate}</b>
            </p>
            <div className="flex gap-2 mt-1">
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                {notification.metadata.leaveType}
              </span>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                Balance: {notification.metadata.leaveBalance}
              </span>
            </div>
            {notification.timestamp && (
              <span className="text-xs italic text-muted-foreground block mt-1">
                Reason: {notification.timestamp}
              </span>
            )}
          </div>

          {notification.metadata.status === "PENDING" &&
          (user?.role === "ADMIN" || user?.role === "PM") ? (
            <div className="flex flex-col gap-2 items-end">
              <Button
                size="sm"
                className="w-20 bg-green-500 hover:bg-green-700 text-white"
                onClick={() => handleLeaveAction(notification.id, "approved")}
              >
                Approve
              </Button>
              <Button
                size="sm"
                className="w-20"
                variant="destructive"
                onClick={() => handleLeaveAction(notification.id, "declined")}
              >
                Decline
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {notification.metadata.status === "APPROVED" ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : notification.metadata.status === "REJECTED" ? (
                <XCircle className="h-5 w-5 text-red-500" />
              ) : (
                <span className="text-xs italic text-muted-foreground">Awaiting manager review</span>
              )}
              {notification.metadata.status && (
                <span className="text-xs text-muted-foreground">{notification.metadata.status}</span>
              )}
            </div>
          )}
        </div>
      );
    }

    // Timesheet & generic notifications
    return (
      <div
        onClick={handleClick}
        className="flex flex-col gap-1 w-full cursor-pointer p-4 border-b group relative"
      >
        <div className="flex justify-between items-start">
          <span className="font-medium pr-6">{notification.title}</span>
          {!notification.read && <span className="h-2 w-2 rounded-full bg-primary shrink-0"></span>}
        </div>
        <p className="text-muted-foreground">{notification.message}</p>
        <span className="text-xs text-muted-foreground">{notification.timestamp}</span>

        {!notification.read && (
          <Button
            size="icon"
            variant="ghost"
            className="absolute top-4 right-4 h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => handleMarkAsRead(notification.id, e)}
            title="Mark as read"
          >
            <CheckCircle size={18} className="text-muted-foreground hover:text-primary" />
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Notifications ({unreadCount} unread)</h2>
        <Button variant="ghost" size="sm" onClick={markAllAsRead} className="gap-2">
          <CheckCheck size={16} />
          Mark all as read
        </Button>
      </div>
      <div className="flex flex-col border rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="p-4 text-center text-muted-foreground text-sm">Loading notifications...</div>
        ) : notifications.length > 0 ? (
          notifications.map((n) => (
            <div key={n.uniqueKey}>{renderNotificationContent(n)}</div>
          ))
        ) : (
          <div className="p-4 text-center text-muted-foreground text-sm">No notifications</div>
        )}
      </div>
      
      {/* Pagination Controls */}
      <div className="flex justify-between items-center mt-4">
        <Button 
          variant="outline" 
          onClick={() => fetchNotifications(pagination.page - 1)}
          disabled={pagination.page <= 1}
        >
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Page {pagination.page} of {pagination.pages}
        </span>
        <Button 
          variant="outline" 
          onClick={() => fetchNotifications(pagination.page + 1)}
          disabled={pagination.page >= pagination.pages}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
